import { useState } from 'react';
import { ComposerPrimitive, useMessageRuntime } from '@assistant-ui/react';
import { ComposerAttachments, ComposerAddAttachment } from './attachment';
import { Button } from '../ui/button';
import { useChatCopy, useChatInterfaceLocale } from '../../lib/chat-i18n';
import { localizeWorkspaceError } from '../../lib/workspace-error-copy';
import { useMessageRepair } from '../../lib/message-repair-context';
import { useNamespaceCopy } from '../../i18n/ns';
import repairCopy from '../../i18n/ns/chat-repair';

/** Save-only repair is independent of billing/model admission. Continuing is
 * a subsequent ordinary Chat Send with its existing permissions and cap. */
export function MessageRepairComposer() {
  const save = useMessageRepair();
  const composer = useMessageRuntime().composer;
  const copy = useChatCopy();
  const locale = useChatInterfaceLocale();
  const repair = useNamespaceCopy(repairCopy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!save || composer.getState().type !== 'edit' || busy) return;
    setBusy(true);
    setError(null);
    try {
      await save(composer);
    } catch (error) {
      setError(
        error instanceof Error ? localizeWorkspaceError(error.message, locale) : repair.failed,
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <ComposerPrimitive.Root
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      className="aui-edit-composer-root border-border/70 ms-auto flex w-full max-w-[85%] flex-col rounded-(--composer-radius) border bg-(--composer-bg) p-3 shadow-(--shadow-soft)"
    >
      <ComposerAttachments />
      <ComposerPrimitive.Input
        dir="auto"
        disabled={busy}
        submitMode="none"
        className="aui-edit-composer-input min-h-14 w-full resize-none bg-transparent text-base outline-none"
      />
      {error && (
        <p role="alert" className="text-destructive mt-2 text-sm">
          {error}
        </p>
      )}
      <p className="text-muted-foreground mt-2 text-xs">{repair.guidance}</p>
      <div className="mt-2 flex items-center gap-2">
        <ComposerAddAttachment />
        <div className="ms-auto flex gap-2">
          <ComposerPrimitive.Cancel asChild>
            <Button type="button" variant="ghost" size="sm" disabled={busy}>
              {copy.cancel}
            </Button>
          </ComposerPrimitive.Cancel>
          <Button type="button" size="sm" disabled={busy} onClick={() => void submit()}>
            {busy ? repair.saving : repair.save}
          </Button>
        </div>
      </div>
    </ComposerPrimitive.Root>
  );
}
